import agentsBlue from '../../../assets/hero-icons/agents/agents-01.svg'; // small blue (1:26243)
import agentsOrange from '../../../assets/hero-icons/agents/agents-02.svg'; // small orange (1:26249)
import agentsPurple from '../../../assets/hero-icons/agents/agents-03.svg'; // small purple (1:26261)
import agentsYellow from '../../../assets/hero-icons/agents/agents-05.svg'; // small yellow (1:26255)
import agentsGreenLg from '../../../assets/hero-icons/agents/agents-06.svg'; // large green (1:26267)
import agentsYellowLg from '../../../assets/hero-icons/agents/agents-07.svg'; // large yellow (1:26255)
import agentsOrangeLg from '../../../assets/hero-icons/agents/agents-08.svg'; // large orange (1:26249)
import agentsPurpleLg from '../../../assets/hero-icons/agents/agents-10.svg'; // large purple (1:26261)
import agentsGreenSm from '../../../assets/hero-icons/agents/agents.svg'; // small green (1:26267)
import asana from '../../../assets/hero-icons/apps/asana.svg';
import atlassianBitbucket from '../../../assets/hero-icons/apps/atlassian-bitbucket.svg';
import atlassianConfluence from '../../../assets/hero-icons/apps/atlassian-confluence.svg';
import atlassianFocus from '../../../assets/hero-icons/apps/atlassian-focus.svg';
import atlassianGoals from '../../../assets/hero-icons/apps/atlassian-goals.svg';
import atlassianJira from '../../../assets/hero-icons/apps/atlassian-jira.svg';
import atlassianJpd from '../../../assets/hero-icons/apps/atlassian-jpd.svg';
import atlassianJsm from '../../../assets/hero-icons/apps/atlassian-jsm.svg';
import atlassianLoom from '../../../assets/hero-icons/apps/atlassian-loom.svg';
import atlassianProjects from '../../../assets/hero-icons/apps/atlassian-projects.svg';
import dropbox from '../../../assets/hero-icons/apps/dropbox.svg';
import figma from '../../../assets/hero-icons/apps/figma.svg';
import github from '../../../assets/hero-icons/apps/github.svg';
import googleCalendar from '../../../assets/hero-icons/apps/google-calendar.svg';
import googleDocs from '../../../assets/hero-icons/apps/google-docs.svg';
import googleDrive from '../../../assets/hero-icons/apps/google-drive.svg';
import googleSheets from '../../../assets/hero-icons/apps/google-sheets.svg';
import launchdarkly from '../../../assets/hero-icons/apps/launchdarkly.svg';
import microsoftOutlook from '../../../assets/hero-icons/apps/microsoft-outlook.svg';
import microsoftSharepoint from '../../../assets/hero-icons/apps/microsoft-sharepoint.svg';
import microsoftTeams from '../../../assets/hero-icons/apps/microsoft-teams.svg';
import rovo from '../../../assets/hero-icons/apps/rovo.svg';
import avatar01 from '../../../assets/hero-icons/avatars/avatar-01.webp';
import avatar02 from '../../../assets/hero-icons/avatars/avatar-02.webp';
import avatar03 from '../../../assets/hero-icons/avatars/avatar-03.webp';
import avatar04 from '../../../assets/hero-icons/avatars/avatar-04.webp';
import avatar05 from '../../../assets/hero-icons/avatars/avatar-05.webp';
import avatar06 from '../../../assets/hero-icons/avatars/avatar-06.webp';
import avatar07 from '../../../assets/hero-icons/avatars/avatar-07.webp';
import avatar08 from '../../../assets/hero-icons/avatars/avatar-08.webp';
import avatar09 from '../../../assets/hero-icons/avatars/avatar-09.webp';
import avatar10 from '../../../assets/hero-icons/avatars/avatar-10.webp';
import avatar11 from '../../../assets/hero-icons/avatars/avatar-11.webp';
import scribbleSquiggle from '../../../assets/hero-icons/scribbles/circle-scribble.webp';
import scribbleArrow from '../../../assets/hero-icons/scribbles/loop-swoosh.webp';
import scribblePlus from '../../../assets/hero-icons/scribbles/plus.webp';
import scribbleRocket from '../../../assets/hero-icons/scribbles/starburst.webp';

import type { HeroViewportTier } from './use-viewport-tier';

export type HeroAssetKind = 'app' | 'agent' | 'avatar' | 'label' | 'scribble';

export type HeroAsset = {
	/** Stable kebab-case id; referenced by IconEdge.from / IconEdge.to */
	id: string;
	/** Imported asset URL. Omitted for label assets rendered with ADS Lozenge. */
	src?: string;
	/** 0..1 normalized center x on the reference canvas */
	x: number;
	/** 0..1 normalized center y on the reference canvas */
	y: number;
	/** 0..1 normalized rendered width relative to REFERENCE_WIDTH */
	width: number;
	/** Category — drives styling tweaks (e.g. avatars get a subtle ring) */
	kind: HeroAssetKind;
	/**
	 * Optional explicit depth in -1..1 (far..near). When omitted, the motion
	 * module derives a depth value from `width` so larger icons read as
	 * "closer" — driving a wider ambient drift amplitude.
	 */
	z?: number;
	/** Optional alt text; defaults to '' (decorative) */
	alt?: string;
};

export type IconEdge = {
	from: string;
	to: string;
};

export const REFERENCE_WIDTH = 1680;
export const REFERENCE_HEIGHT = 1200;
export const MOBILE_REFERENCE_WIDTH = 650;
export const MOBILE_REFERENCE_HEIGHT = 1200;
export const MOBILE_ICON_SCALE = 0.65;

/**
 * Convert a Figma bounding box (top-left x, y plus width, height in the
 * 1680x1200 source frame) into the normalized center + width our renderer
 * expects.
 *
 *   - x, y → top-left of the box in Figma px
 *   - w, h → rendered size in Figma px (h is unused but kept for clarity)
 *
 * Returns `{ x, y, width }` ready to spread into a HeroAsset, where each
 * value is expressed as a 0..1 fraction of the reference frame
 * (REFERENCE_WIDTH × REFERENCE_HEIGHT). The icon layer pins those
 * dimensions in pixels at render time, so each asset ends up at exactly
 * its authored size and position regardless of viewport size.
 */
const place = (x: number, y: number, w: number, h: number) => ({
	x: (x + w / 2) / REFERENCE_WIDTH,
	y: (y + h / 2) / REFERENCE_HEIGHT,
	width: w / REFERENCE_WIDTH,
});

/**
 * Updated hero assets, transcribed from the portable prototype. Desktop and
 * compact tiers share this catalog; compact scales the oval around the text.
 */
export const HERO_ASSETS: HeroAsset[] = [
	{ id: 'agent-green-large-top', src: agentsGreenLg, ...place(391, 44, 52.5, 60), kind: 'agent' },
	{ id: 'avatar-ali', src: avatar01, ...place(1337, 998, 64, 64), kind: 'avatar' },
	{ id: 'google-drive', src: googleDrive, ...place(1181, 137, 60, 60), kind: 'app' },
	{ id: 'avatar-keanu-small', src: avatar02, ...place(367, 161, 40, 40), kind: 'avatar' },
	{ id: 'figma', src: figma, ...place(528, 120, 48, 48), kind: 'app' },
	{ id: 'status-done', ...place(1161, 1037, 68, 32), kind: 'label', alt: 'Done' },
	{ id: 'avatar-avery', src: avatar03, ...place(657, 46, 64, 64), kind: 'avatar' },
	{
		id: 'agent-blue-small-upper-right',
		src: agentsBlue,
		...place(1287, 212, 28, 32),
		kind: 'agent',
	},
	{ id: 'atlassian-loom', src: atlassianLoom, ...place(892, 31, 60, 60), kind: 'app' },
	{
		id: 'scribble-starburst',
		src: scribbleRocket,
		...place(230, 170, 36.313, 37.01),
		kind: 'scribble',
	},
	{ id: 'scribble-plus', src: scribblePlus, ...place(1078, 102, 22.973, 25), kind: 'scribble' },
	{ id: 'rovo', src: rovo, ...place(344, 995, 60, 60), kind: 'app', alt: 'Rovo' },
	{ id: 'atlassian-confluence', src: atlassianConfluence, ...place(714, 146, 48, 48), kind: 'app' },
	{ id: 'agent-purple-large', src: agentsPurpleLg, ...place(678, 1061, 52.5, 60), kind: 'agent' },
	{ id: 'microsoft-teams', src: microsoftTeams, ...place(527, 1108, 32, 32), kind: 'app' },
	{
		id: 'agent-orange-small-upper-right',
		src: agentsOrange,
		...place(1212, 26, 28, 32),
		kind: 'agent',
	},
	{
		id: 'status-on-track',
		...place(1440, 113, 108, 32),
		kind: 'label',
		alt: 'On track',
	},
	{ id: 'avatar-ash', src: avatar04, ...place(1475, 267, 64, 64), kind: 'avatar' },
	{ id: 'atlassian-goals', src: atlassianGoals, ...place(1015, 147, 40, 40), kind: 'app' },
	{ id: 'agent-blue-small-mid', src: agentsBlue, ...place(370, 245, 28, 32), kind: 'agent' },
	{ id: 'avatar-aspen', src: avatar05, ...place(1362, 174, 64, 64), kind: 'avatar' },
	{ id: 'status-to-do', ...place(241, 256, 72, 32), kind: 'label', alt: 'To do' },
	{ id: 'agent-yellow-small-left', src: agentsYellow, ...place(106, 217, 28, 32), kind: 'agent' },
	{ id: 'atlassian-projects', src: atlassianProjects, ...place(141, 316, 48, 48), kind: 'app' },
	{ id: 'agent-green-small-right', src: agentsGreenSm, ...place(1599, 263, 28, 32), kind: 'agent' },
	{
		id: 'scribble-circle',
		src: scribbleSquiggle,
		...place(1453, 388, 41.169, 34.251),
		kind: 'scribble',
	},
	{ id: 'atlassian-focus', src: atlassianFocus, ...place(208, 529, 32, 32), kind: 'app' },
	{ id: 'asana', src: asana, ...place(297, 449, 32, 32), kind: 'app' },
	{ id: 'google-calendar', src: googleCalendar, ...place(1369, 469, 48, 48), kind: 'app' },
	{ id: 'agent-yellow-large', src: agentsYellowLg, ...place(1500, 500, 52.5, 60), kind: 'agent' },
	{ id: 'avatar-quinn', src: avatar06, ...place(287, 586, 64, 64), kind: 'avatar' },
	{ id: 'atlassian-jira', src: atlassianJira, ...place(352, 721, 60, 60), kind: 'app' },
	{ id: 'agent-orange-large', src: agentsOrangeLg, ...place(1177, 892, 52.5, 60), kind: 'agent' },
	{ id: 'avatar-natalie-small', src: avatar07, ...place(1308, 75, 40, 40), kind: 'avatar' },
	{ id: 'scribble-loop-swoosh', src: scribbleArrow, ...place(1342, 625, 79, 27), kind: 'scribble' },
	{ id: 'microsoft-sharepoint', src: microsoftSharepoint, ...place(152, 656, 48, 48), kind: 'app' },
	{ id: 'google-sheets', src: googleSheets, ...place(89, 450, 60, 60), kind: 'app' },
	{ id: 'agent-blue-small-mid-right', src: agentsBlue, ...place(485, 944, 28, 32), kind: 'agent' },
	{ id: 'avatar-keanu', src: avatar08, ...place(998, 1041, 64, 64), kind: 'avatar' },
	{
		id: 'status-at-risk',
		...place(168, 785, 88, 32),
		kind: 'label',
		alt: 'At risk',
	},
	{ id: 'github', src: github, ...place(1398, 854, 60, 60), kind: 'app' },
	{ id: 'atlassian-jpd', src: atlassianJpd, ...place(1514, 643, 60, 60), kind: 'app' },
	{ id: 'avatar-andrea-small', src: avatar09, ...place(841, 154, 40, 40), kind: 'avatar' },
	{ id: 'launchdarkly', src: launchdarkly, ...place(108, 868, 60, 60), kind: 'app' },
	{ id: 'avatar-naz', src: avatar10, ...place(579, 957, 64, 64), kind: 'avatar' },
	{ id: 'agent-purple-small-bottom', src: agentsPurple, ...place(248, 930, 28, 32), kind: 'agent' },
	{ id: 'atlassian-bitbucket', src: atlassianBitbucket, ...place(800, 970, 60, 60), kind: 'app' },
	{ id: 'microsoft-outlook', src: microsoftOutlook, ...place(1508, 921, 60, 60), kind: 'app' },
	{ id: 'avatar-imani', src: avatar11, ...place(315, 830, 64, 64), kind: 'avatar' },
	{ id: 'dropbox', src: dropbox, ...place(1444, 770, 48, 48), kind: 'app' },
	{
		id: 'placeholder-gray-lower-middle',
		src: atlassianJsm,
		...place(1029, 914, 40, 40),
		kind: 'app',
		alt: 'Jira Service Management',
	},
	{
		id: 'placeholder-gray-right-middle',
		src: googleDocs,
		...place(1316, 813, 40, 40),
		kind: 'app',
		alt: 'Google Docs',
	},
];

/**
 * Reserved central area for the hero text block (heading + sub + CTA),
 * expressed in REFERENCE_WIDTH x REFERENCE_HEIGHT coordinates. Edge
 * generation in `./edges.ts` rejects any segment that crosses this rect.
 */
export const TEXT_SAFE_AREA = {
	x: [560, 1120] as const,
	y: [420, 720] as const,
};

export const MOBILE_TEXT_SAFE_AREA = {
	x: [125, 525] as const,
	y: [390, 800] as const,
};

/**
 * Lookup helper. Linear-scans HERO_ASSETS for a matching id; HERO_ASSETS
 * is small (~50 items) so we don't bother memoising into a Map.
 */
export const findAsset = (id: string): HeroAsset | undefined =>
	HERO_ASSETS.find((a) => a.id === id);

type TextSafeArea = {
	x: readonly [number, number];
	y: readonly [number, number];
};

const getMotionOrigin = (
	textSafeArea: TextSafeArea,
	referenceWidth: number,
	referenceHeight: number,
) => ({
	x: (textSafeArea.x[0] + textSafeArea.x[1]) / 2 / referenceWidth,
	y: (textSafeArea.y[0] + textSafeArea.y[1]) / 2 / referenceHeight,
});

export const MOTION_ORIGIN: {
	x: number;
	y: number;
} = getMotionOrigin(TEXT_SAFE_AREA, REFERENCE_WIDTH, REFERENCE_HEIGHT);
export const MOBILE_MOTION_ORIGIN: {
	x: number;
	y: number;
} = getMotionOrigin(MOBILE_TEXT_SAFE_AREA, MOBILE_REFERENCE_WIDTH, MOBILE_REFERENCE_HEIGHT);

const scaleAssetsAroundOrigin = (
	assets: readonly HeroAsset[],
	xScale: number,
	yScale: number,
	origin: { x: number; y: number },
): HeroAsset[] => {
	if (xScale === 1 && yScale === 1) return [...assets];
	return assets.map((asset) => ({
		...asset,
		x: origin.x + (asset.x - origin.x) * xScale,
		y: origin.y + (asset.y - origin.y) * yScale,
	}));
};

const placeMobile = (x: number, y: number, w: number, h: number) => ({
	x: (x + w / 2) / MOBILE_REFERENCE_WIDTH,
	y: (y + h / 2) / MOBILE_REFERENCE_HEIGHT,
	width: (w * MOBILE_ICON_SCALE) / MOBILE_REFERENCE_WIDTH,
});

const mobileAsset = (id: string, x: number, y: number, w: number, h: number): HeroAsset => {
	const asset = HERO_ASSETS.find((a) => a.id === id);
	if (!asset) throw new Error(`Missing hero icon asset: ${id}`);
	return { ...asset, ...placeMobile(x, y, w, h) };
};

export const HERO_ASSETS_MOBILE: HeroAsset[] = [
	mobileAsset('agent-green-large-top', 104, 174, 52.5, 60),
	mobileAsset('avatar-ali', 260, 148, 64, 64),
	mobileAsset('google-drive', 430, 196, 60, 60),
	mobileAsset('figma', 178, 254, 48, 48),
	mobileAsset('atlassian-loom', 330, 242, 60, 60),
	mobileAsset('microsoft-teams', 72, 296, 32, 32),
	mobileAsset('rovo', 508, 296, 60, 60),
	mobileAsset('scribble-plus', 288, 340, 23, 25),
	mobileAsset('status-to-do', 42, 370, 72, 32),
	mobileAsset('avatar-natalie-small', 434, 290, 40, 40),
	mobileAsset('agent-blue-small-upper-right', 562, 386, 28, 32),
	mobileAsset('agent-yellow-small-left', 80, 488, 28, 32),
	mobileAsset('placeholder-gray-right-middle', 560, 464, 40, 40),
	mobileAsset('atlassian-focus', 48, 594, 32, 32),
	mobileAsset('scribble-circle', 574, 560, 41, 34),
	mobileAsset('asana', 586, 640, 32, 32),
	mobileAsset('atlassian-projects', 74, 710, 48, 48),
	mobileAsset('google-calendar', 548, 704, 48, 48),
	mobileAsset('status-at-risk', -18, 782, 88, 32),
	mobileAsset('avatar-quinn', 70, 842, 64, 64),
	mobileAsset('atlassian-jira', 206, 850, 60, 60),
	mobileAsset('agent-orange-large', 424, 840, 52.5, 60),
	mobileAsset('github', 546, 862, 60, 60),
	mobileAsset('atlassian-bitbucket', 316, 928, 60, 60),
	mobileAsset('dropbox', 394, 958, 48, 48),
	mobileAsset('google-sheets', 92, 966, 60, 60),
	mobileAsset('scribble-loop-swoosh', 514, 976, 79, 27),
	mobileAsset('placeholder-gray-lower-middle', 184, 1030, 40, 40),
	mobileAsset('agent-purple-small-bottom', 284, 1054, 28, 32),
	mobileAsset('microsoft-outlook', 540, 1026, 60, 60),
	mobileAsset('avatar-naz', 452, 1060, 64, 64),
	mobileAsset('status-done', 374, 1086, 68, 32),
];

export type ResolvedHeroLayout = {
	assets: HeroAsset[];
	referenceWidth: number;
	referenceHeight: number;
	textSafeArea: TextSafeArea;
	motionOrigin: { x: number; y: number };
};

const TIER_OVAL_OVERRIDES: Record<
	HeroViewportTier,
	Partial<{ ovalSize: number; ovalAspectRatio: number }>
> = {
	desktop: {},
	compact: {
		ovalSize: 0.77,
		ovalAspectRatio: 0.96,
	},
	mobile: {},
};

export function getHeroLayout({
	viewportTier,
	ovalSize,
	ovalAspectRatio,
	mobileOvalSize,
	mobileOvalAspectRatio,
}: {
	viewportTier: HeroViewportTier;
	ovalSize: number;
	ovalAspectRatio: number;
	mobileOvalSize: number;
	mobileOvalAspectRatio: number;
}): ResolvedHeroLayout {
	if (viewportTier === 'mobile') {
		return {
			assets: scaleAssetsAroundOrigin(
				HERO_ASSETS_MOBILE,
				mobileOvalSize * mobileOvalAspectRatio,
				mobileOvalSize,
				MOBILE_MOTION_ORIGIN,
			),
			referenceWidth: MOBILE_REFERENCE_WIDTH,
			referenceHeight: MOBILE_REFERENCE_HEIGHT,
			textSafeArea: MOBILE_TEXT_SAFE_AREA,
			motionOrigin: MOBILE_MOTION_ORIGIN,
		};
	}

	const overrides = TIER_OVAL_OVERRIDES[viewportTier];
	const effectiveOvalSize = overrides.ovalSize ?? ovalSize;
	const effectiveOvalAspectRatio = overrides.ovalAspectRatio ?? ovalAspectRatio;

	return {
		assets: scaleAssetsAroundOrigin(
			HERO_ASSETS,
			effectiveOvalSize * effectiveOvalAspectRatio,
			effectiveOvalSize,
			MOTION_ORIGIN,
		),
		referenceWidth: REFERENCE_WIDTH,
		referenceHeight: REFERENCE_HEIGHT,
		textSafeArea: TEXT_SAFE_AREA,
		motionOrigin: MOTION_ORIGIN,
	};
}
